import { kebabCase, snakeCase } from 'lodash';
import { useCallback, useMemo } from 'react';

import FormCaseAction from 'dashboard/forms/FormCaseAction';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Account, AccountAttributes } from 'dashboard/models/account';
import { Case, CaseActionWorkflow, CaseAttributes, CaseDataManager } from 'dashboard/models/case';
import { Inquiry, InquiryAttributes } from 'dashboard/models/inquiry';
import {
  Verification,
  VerificationGovernmentId,
  VerificationGovernmentIdAttributes,
} from 'dashboard/models/verification';
import { FieldHash, FieldString, FieldType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { Json } from 'lib/types';
import { VerificationStatus } from 'lib/verification';

// path is an array of strings like ['address', 'street1']
const getNestedHashField: any = (hashField: FieldHash, path: string[]) => {
  if (path.length === 1) {
    return hashField.value[path[0]]?.value;
  } else if (hashField.value[path[0]] && hashField.value[path[0]].type === FieldType.Hash) {
    return getNestedHashField(hashField.value[path[0]], path.slice(1));
  }
};

// path is in the form ['case', 'fields', 'field-name', 'nested-field-name']
export const getCasePrefillValue: any = (kase: Case, path: string[]) => {
  const kebabPath = path.map((data) => kebabCase(data));
  if (path[1] === 'id') {
    return kase?.id;
  } else if (path[1] === 'fields' && kebabPath.length >= 3) {
    const fieldName = kebabPath[2];
    const field = kase?.attributes?.fields?.[fieldName];
    if (kebabPath.length > 3 && field && field.type === FieldType.Hash) {
      return getNestedHashField(field, kebabPath.slice(3));
    } else {
      return field?.value;
    }
  } else if (kase?.attributes?.[path[1] as keyof CaseAttributes]) {
    // pull value from case attribute
    return kase?.attributes?.[path[1] as keyof CaseAttributes];
  }
};

interface CaseActionModalV2Props {
  case: Case;
  inquiries: Inquiry[];
  accounts: Account[];
  verifications: Verification[];
  caseDataManager: CaseDataManager;
  caseAction: CaseActionWorkflow;
  handleRunWorkflow: (values: Json, workflowId: string) => Promise<void>;
  hideModal: () => void;
}

const CaseActionModalV2 = ({
  case: kase,
  inquiries,
  accounts,
  verifications,
  caseDataManager,
  caseAction,
  handleRunWorkflow,
  hideModal,
}: CaseActionModalV2Props) => {
  const handleRunWorkflowAndClose = useCallback(
    async (values: Json) => {
      hideModal();
      await handleRunWorkflow(values, caseAction.workflow.id);
    },
    [handleRunWorkflow],
  );

  // We currently prefill with the first inquiry or first account (first object of its kind)
  // associated with a case.
  // Longer term, we don't want to do that since we don't actually know how many inquiries
  // or accounts will be present on a case, and which one the user wants to use for the prefill.
  // Ideally, we'll be able to have the specific inquiry or account (or report or verification, etc)
  // as a selected option that can then be used to prefill the rest of the input modal.
  const initialValues = useMemo(() => {
    return caseAction.trigger.attributes['param-schema'].reduce<Record<string, unknown>>(function (
      values,
      schema,
    ) {
      const { prefill, name } = schema;
      if (prefill == null || prefill === '') return values;

      // The structure for prefilled data that references an object field is `object_type.field_name`.
      // e.g. `inquiry.name-first`. Fields with empty or invalid prefill values will be skipped.
      // Otherwise, we assume that the prefill value requires no calculation and should be prefilled
      // as is.
      // TODO: (melissa) change this to use case data manager!
      const prefillData = prefill.split('.');
      const prefillSource = prefillData[0];
      const governmentIdVerifications: VerificationGovernmentId[] = verifications.filter(
        (v): v is VerificationGovernmentId => {
          return v.type === ApiModel.VerificationGovernmentId;
        },
      );
      const passedGovIdVerifications = governmentIdVerifications.filter(
        (v) => v.attributes.status === VerificationStatus.Passed,
      );

      if (prefillData.length >= 2) {
        const account = accounts[0];
        const inquiry = inquiries[0];
        const verification = passedGovIdVerifications[0];
        const prefillField = prefillData[1];
        switch (prefillSource) {
          case 'account':
            if (prefillField === PiiAttributes.Address) {
              const fieldAddress = account?.attributes?.fields?.address?.value as Record<
                string,
                FieldString
              >;
              values[name] = {
                street1: fieldAddress?.street_1?.value,
                street2: fieldAddress?.street_2?.value,
                city: fieldAddress?.city?.value,
                subdivision: fieldAddress?.subdivision?.value,
                postalCode: fieldAddress?.postal_code?.value,
                countryCode: fieldAddress?.country_code?.value ?? 'US',
              };
            } else if (prefillField === PiiAttributes.NameFirst) {
              const fieldName = account?.attributes?.fields?.name?.value as Record<
                string,
                FieldString
              >;
              values[name] = fieldName?.first?.value;
            } else if (prefillField === PiiAttributes.NameMiddle) {
              const fieldName = account?.attributes?.fields?.name?.value as Record<
                string,
                FieldString
              >;
              values[name] = fieldName?.middle?.value;
            } else if (prefillField === PiiAttributes.NameLast) {
              const fieldName = account?.attributes?.fields?.name?.value as Record<
                string,
                FieldString
              >;
              values[name] = fieldName?.last?.value;
            } else if (prefillField === PiiAttributes.CountryCode) {
              const fieldAddress = account?.attributes?.fields?.address?.value as Record<
                string,
                FieldString
              >;
              values[name] = fieldAddress?.country_code?.value;
            } else if (prefillField === PiiAttributes.Birthdate) {
              values[name] = account?.attributes?.fields?.birthdate?.value;
            } else if (prefillField === PiiAttributes.PhoneNumber) {
              values[name] = account?.attributes?.fields?.phone_number?.value;
            } else if (prefillField === PiiAttributes.EmailAddress) {
              values[name] = account?.attributes?.fields?.email_address?.value;
            } else if (prefillField === 'id') {
              values[name] = account?.id;
            } else {
              values[name] = account?.attributes?.[prefillField as keyof AccountAttributes];
            }
            break;
          case 'inquiry':
            if (prefillField === PiiAttributes.Address) {
              values[name] = {
                street1: inquiry?.attributes?.['address-street-1'],
                street2: inquiry?.attributes?.['address-street-2'],
                city: inquiry?.attributes?.['address-city'],
                subdivision: inquiry?.attributes?.['address-subdivision'],
                postalCode: inquiry?.attributes?.['address-postal-code'],
                countryCode: inquiry?.attributes?.fields['address-country-code'] ?? 'US',
              };
            } else if (prefillField === 'id') {
              values[name] = inquiry?.id;
            } else if (inquiry?.attributes?.[prefillField as keyof InquiryAttributes]) {
              // pull value from inquiry attribute
              values[name] = inquiry?.attributes?.[prefillField as keyof InquiryAttributes];
            } else if (inquiry?.attributes?.fields?.[prefillField]) {
              // pull value from inquiry fields
              values[name] = inquiry?.attributes?.fields?.[prefillField]?.value;
            }
            break;
          case 'verification/government-id':
            // pull value from first passed govId attributes:
            if (prefillField === PiiAttributes.Address) {
              values[name] = {
                street1: verification?.attributes?.['address-street-1'],
                street2: verification?.attributes?.['address-street-2'],
                city: verification?.attributes?.['address-city'],
                subdivision: verification?.attributes?.['address-subdivision'],
                postalCode: verification?.attributes?.['address-postal-code'],
              };
            } else if (prefillField === 'id') {
              values[name] = verification?.id;
            } else {
              values[name] =
                verification?.attributes?.[
                  prefillField as keyof Partial<VerificationGovernmentIdAttributes>
                ];
            }
            break;
          case 'case':
            values[name] = getCasePrefillValue(kase, prefillData);
            break;
          case 'current-user':
          case 'current_user':
            values[name] = caseDataManager['current_user']?.[snakeCase(prefillField) as keyof Json];
            break;
          default:
            break;
        }
      } else {
        values[name] = prefill;
      }
      return values;
    }, {});
  }, [caseAction]);

  return (
    <ModalContainer data-test='case-action-modal-v2'>
      <ModalHeader data-test='case-action-modal-v2-header' onClose={hideModal}>
        {caseAction.workflow.attributes.name}
      </ModalHeader>
      <ModalBody>
        <FormCaseAction
          caseAction={caseAction}
          onSubmit={handleRunWorkflowAndClose}
          handleCancel={hideModal}
          initialValues={initialValues}
        />
      </ModalBody>
    </ModalContainer>
  );
};

export default CaseActionModalV2;
